import React, {
  useState,
  useEffect,
} from 'react';
import axios from 'axios';
import Moment from 'react-moment';
import {
  makeStyles,
  createStyles,
} from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  green,
  red,
} from '@material-ui/core/colors';
import CircularProgress from '@material-ui/core/CircularProgress';
import CheckIcon from '@material-ui/icons/Check';
import RemoveIcon from '@material-ui/icons/Remove';
import ClearIcon from '@material-ui/icons/Clear';

const useStyles = makeStyles(theme => createStyles({
  root: {
    color: '#212121',
    backgroundColor: '#fafafa',
    borderTop: '1px solid #bdbdbd',
  },
  description: {
    color: '#212121',
    padding: 20,
  },
  noLessonsAlert: {
    padding: 20,
  },
  tableResponsive: {
    display: 'block',
    width: '100%',
    overflowX: 'auto',
    '-webkit-overflow-scrolling': 'touch',
    '-ms-overflow-style': '-ms-autohiding-scrollbar',
  },
  table: {
    width: '100%',
    '& tr th': {
      backgroundColor: '#ffffff',
      color: '#616161',
      fontWeight: 'normal',
      fontSize: '0.8em',
    },
    '& tr td': {
      borderTop: '1px solid #e6ebed',
    },
    '& tr:nth-child(even) td': {
      backgroundColor: '#ffffff',
    },
    '& tr th, & tr td': {
      padding: 10,
    },
  },
  textAlignCenter: {
    textAlign: 'center',
  },
  iconActive: {
    color: green[500],
  },
  iconInactive: {
    color: red[500],
  },
}));

export default function ReportCardsDetailsTarget(props) {
  const classes = useStyles();
  const { t } = useTranslation(undefined, { useSuspense: false });
  const selectedPupil = useSelector(state => state.selectedPupil);
  const {
    id,
    data: rawData,
    selectedDate,
  } = props;
  const [isLoading, setLoading] = useState(false);
  const [isLoaded, setLoaded] = useState(false);
  const [data, setData] = useState(rawData);
  const [error, setError] = useState(null);
  const [dataPeriods, setDataPeriods] = useState([]);

  let axiosCancel = null;

  useEffect(() => {
    load();

    return () => {
      if (axiosCancel) {
        axiosCancel();
      }
    };
  }, [selectedDate, id]);

  const load = () => {
    if (axiosCancel) {
      axiosCancel();
    }

    setLoading(true);

    axios
      .get(
        `${window.apiUrl}/getpupilreportcardtarget/${id}?date=${selectedDate}&pupil_id=${selectedPupil.id}`,
        {
          cancelToken: new axios.CancelToken((c) => { axiosCancel = c }),
        }
      )
      .then((response) => {
        if (response.data.success) {
          setData(response.data.data.target);
          setDataPeriods(response.data.data.periods);
        } else {
          setError(response.data.error);
        }

        setLoading(false);
        setLoaded(true);
      })
      .catch((thrown) => {});
  };

  const isMarkRanking = data?.pupil_report_card?.required_teacher_action === 'ranking';

  return (
    <div className={`report-cards-details-target ${classes.root}`}>
      {isLoading && (
        <CircularProgress />
      )}
      {!isLoading && isLoaded && error && (
        <>
          {error}
        </>
      )}
      {!isLoading && isLoaded && !error && (
        <>
          <div className={classes.description}>
            {data.description}
          </div>
          {dataPeriods.length === 0 && (
            <div className={classes.noLessonsAlert}>
              {t('No lessons scheduled for this day')}
            </div>
          )}
          {dataPeriods.length > 0 && (
          <div className={classes.tableResponsive}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th width="100">{t('Period')}</th>
                  <th width="80">{t('Achieved')}</th>
                  <th>{t('Comment')}</th>
                </tr>
              </thead>
              <tbody>
                {dataPeriods.map((period) => {
                  const periodState = period.mark_id
                    ? (period.met === 'yes'
                      ? 'active'
                      : 'inactive'
                    )
                    : 'default';

                  return (
                    <tr key={period.id}>
                      <td>{period.label}</td>
                      <td className={classes.textAlignCenter}>
                        {isMarkRanking && (
                          <>{period.mark_ranking}</>
                        )}
                        {!isMarkRanking && (
                          <>
                            {periodState === 'default' && (
                              <RemoveIcon />
                            )}
                            {periodState === 'active' && (
                              <CheckIcon className={classes.iconActive} />
                            )}
                            {periodState === 'inactive' && (
                              <ClearIcon className={classes.iconInactive} />
                            )}
                          </>
                        )}
                      </td>
                      <td>
                        <span>{period.comment_description} </span>
                        {period.teacher_name && (
                          <small>(<Moment format={dateFormat + ' HH:mm'}>{period.comment_created}</Moment> {t('by')} {period.teacher_name})</small>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </>
      )}
    </div>
  );
}
